import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MAX_PANEL_RATE, bulkDiscountMessage, discountPct, formatDiscount, isValidDiscount, normaliseRate, planBulkDiscount, rateFromDiscount } from '../../../utils/rateDiscount';
import masterService from '../../../services/masterService';
import ConfirmModal from '../components/ConfirmModal';
import CommonPagination from '../../../components/common/CommonPagination';
import usePagination from '../../../hooks/usePagination';
import './PanelWiseRateTab.css';

// The four ways to look at the tariff. countKey is the key in the counts of the loaded rows.
const TARIFF_VIEWS = [
  { key: 'ALL', label: 'All Items', icon: '🔬', className: 'pwr-view-all', countKey: 'all' },
  { key: 'TEST', label: 'Single Tests', icon: '🧪', className: 'pwr-view-test', countKey: 'test' },
  { key: 'PROFILE', label: 'Clinical Profiles', icon: '📋', className: 'pwr-view-profile', countKey: 'profile' },
  { key: 'PACKAGE', label: 'Health Packages', icon: '📦', className: 'pwr-view-package', countKey: 'package' },
];

/**
 * Discount of one row: shows how much the rate is below the standard MRP and lets the user type a percentage.
 * While typing, the typed text stays as it is (the rate is whole rupees, so the percentage it implies can differ slightly);
 * on leaving the field it shows the percentage the rate really represents.
 */
export const DiscountCell = ({ mrp, rate, highlighted, label, onApply }) => {
  const [draft, setDraft] = React.useState(null);
  const pct = discountPct(mrp, rate);
  if (pct === null) {
    return <span style={{ color: '#94a3b8' }} title="This item has no MRP to take a discount from">—</span>;
  }
  const shown = draft ?? formatDiscount(mrp, rate).replace('%', '');
  const invalid = draft !== null && draft !== '' && !isValidDiscount(draft);
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <input
        type="text"
        inputMode="decimal"
        aria-label={`Discount percent for ${label}`}
        style={{
          width: 64, padding: '6px 8px', textAlign: 'right', fontWeight: 600, fontSize: '0.88rem', borderRadius: 6,
          border: invalid ? '2px solid #dc2626' : highlighted ? '2px solid #f59e0b' : '1px solid #cbd5e1',
          backgroundColor: highlighted ? '#ffffff' : '#f8fafc', color: pct > 0 ? '#047857' : pct < 0 ? '#b91c1c' : '#334155'
        }}
        value={shown}
        onFocus={(e) => { setDraft(formatDiscount(mrp, rate).replace('%', '').replace('—', '')); e.target.select(); }}
        onChange={(e) => {
          const text = e.target.value;
          setDraft(text);
          if (isValidDiscount(text)) onApply(Number(text));
        }}
        onBlur={() => setDraft(null)}
      />
      <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>%</span>
    </div>
  );
};

export const PanelWiseRateTab = ({ showToast, initialPanelId = null }) => {
  // Panels list for dropdown
  const [panels, setPanels] = useState([]);
  const [panelsLoading, setPanelsLoading] = useState(false);
  const [selectedPanelId, setSelectedPanelId] = useState(initialPanelId || 1);

  // Departments for filtering
  const [departments, setDepartments] = useState([]);

  // Bifurcation Item Type Filter: 'ALL' | 'TEST' | 'PROFILE' | 'PACKAGE'
  const [itemTypeFilter, setItemTypeFilter] = useState('ALL');

  // Rates data
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedDept, setSelectedDept] = useState('all');

  // Modified rows: { [itemId]: { itemId, rate: number, mrpRate: number, itemDisplayName: string, itemCode: string } }
  const [modifiedRows, setModifiedRows] = useState({});

  // Selection for bulk actions
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());

  // Bulk rate inputs
  const [bulkRateInput, setBulkRateInput] = useState('');
  const [bulkDiscountInput, setBulkDiscountInput] = useState('');
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkActionType] = useState('fixed'); // 'fixed' | 'percent'
  const [saving, setSaving] = useState(false);

  // 1. Fetch Panels and Departments on mount
  useEffect(() => {
    const loadLookups = async () => {
      setPanelsLoading(true);
      try {
        const [panelRes, deptRes] = await Promise.all([
          masterService.getPanels({ isActive: true }),
          masterService.getDepartments({ isActive: true })
        ]);

        if (panelRes.success && panelRes.data) {
          setPanels(panelRes.data);
          if (!selectedPanelId && panelRes.data.length > 0) {
            setSelectedPanelId(panelRes.data[0].panelId);
          }
        }
        if (deptRes.success && deptRes.data) {
          setDepartments(deptRes.data);
        }
      } catch (err) {
        showToast?.('Failed to load panels and departments.', 'error');
      } finally {
        setPanelsLoading(false);
      }
    };

    loadLookups();
  }, [showToast, selectedPanelId]);

  // 2. Fetch Rates for Selected Panel (supports itemType)
  const fetchPanelRates = useCallback(async () => {
    if (!selectedPanelId) return;
    setLoading(true);
    try {
      const res = await masterService.getPanelRates(selectedPanelId, {
        subCategoryId: selectedDept === 'all' ? undefined : Number(selectedDept),
        search: search.trim() || undefined,
        itemType: itemTypeFilter !== 'ALL' ? itemTypeFilter : undefined
      });
      if (res.success && res.data) {
        setRates(res.data);
        setModifiedRows({});
        setSelectedItemIds(new Set());
      }
    } catch (err) {
      showToast?.('Failed to load panel rate tariff schedule.', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedPanelId, selectedDept, search, itemTypeFilter, showToast]);

  useEffect(() => {
    fetchPanelRates();
  }, [fetchPanelRates]);

  // Current selected panel metadata
  const currentPanel = useMemo(() => {
    return panels.find(p => p.panelId === Number(selectedPanelId)) || null;
  }, [panels, selectedPanelId]);

  // Category counts from current rates or for tabs
  const typeCounts = useMemo(() => {
    let testCount = 0;
    let profileCount = 0;
    let packageCount = 0;

    rates.forEach(r => {
      const type = (r.itemType || 'TEST').toUpperCase();
      if (type === 'PACKAGE') packageCount++;
      else if (type === 'PROFILE') profileCount++;
      else testCount++;
    });

    return {
      all: rates.length,
      test: testCount,
      profile: profileCount,
      package: packageCount
    };
  }, [rates]);

  // Handle single item rate change
  const handleRateChange = (item, newRateVal) => {
    const val = normaliseRate(newRateVal);
    if (val === null) return;

    setModifiedRows(prev => ({
      ...prev,
      [item.itemId]: {
        itemId: item.itemId,
        rate: val,
        mrpRate: item.mrpRate,
        itemDisplayName: item.itemDisplayName || item.investigationName,
        itemCode: item.testCode
      }
    }));
  };

  // Toggle selection for bulk actions
  const toggleSelectItem = (itemId) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItemIds.size === rates.length) {
      setSelectedItemIds(new Set());
    } else {
      setSelectedItemIds(new Set(rates.map(r => r.itemId)));
    }
  };

  // Bulk Apply Handler
  const handleApplyBulk = () => {
    if (selectedItemIds.size === 0) return;

    if (bulkActionType === 'fixed') {
      const rate = normaliseRate(bulkRateInput);
      if (rate === null) {
        showToast?.(`Please enter a rate from 0 to ${MAX_PANEL_RATE}.`, 'error');
        return;
      }
      setModifiedRows(prev => {
        const next = { ...prev };
        rates.forEach(r => {
          if (selectedItemIds.has(r.itemId)) {
            next[r.itemId] = {
              itemId: r.itemId,
              rate: rate,
              mrpRate: r.mrpRate,
              itemDisplayName: r.itemDisplayName || r.investigationName,
              itemCode: r.testCode
            };
          }
        });
        return next;
      });
      showToast?.(`Applied ₹${rate} to ${selectedItemIds.size} items. Click 'Save Rates' to persist.`, 'info');
    } else if (bulkActionType === 'percent') {
      const pct = isValidDiscount(String(bulkDiscountInput).trim()) ? Number(String(bulkDiscountInput).trim()) : NaN;
      if (isNaN(pct)) {
        showToast?.('Please enter a discount percentage between 0 and 100.', 'error');
        return;
      }
      const { updates, skipped } = planBulkDiscount(rates, selectedItemIds, pct);
      setModifiedRows(prev => {
        const next = { ...prev };
        updates.forEach(u => { next[u.itemId] = u; });
        return next;
      });
      showToast?.(bulkDiscountMessage(pct, updates.length, skipped), 'info');
    }
    setShowBulkConfirm(false);
  };

  // Save All Dirty Changes
  const handleSaveAll = async () => {
    const dirtyItems = Object.values(modifiedRows);
    if (dirtyItems.length === 0) {
      showToast?.('No changes to save.', 'info');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        panelId: Number(selectedPanelId),
        items: dirtyItems
      };
      const res = await masterService.batchUpdatePanelRates(selectedPanelId, payload);
      if (res.success) {
        showToast?.(`Successfully updated ${dirtyItems.length} tariff rates for panel!`, 'success');
        await fetchPanelRates();
      } else {
        showToast?.(res.message || 'Failed to save rates.', 'error');
      }
    } catch (err) {
      showToast?.(err.response?.data?.message || 'Error saving panel rates.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const dirtyCount = Object.keys(modifiedRows).length;

  // Switching the view reloads the rows, so unsaved changes would be lost: ask first.
  const switchView = (key) => {
    if (key === itemTypeFilter) return;
    if (dirtyCount > 0 && !window.confirm('You have unsaved rate changes. Switching views will discard them. Continue?')) return;
    setItemTypeFilter(key);
  };

  // The grid shows one page at a time (a panel can have well over a thousand items). Selection and unsaved changes belong to the
  // items, not to the page, so they survive moving between pages.
  const { page, pageSize, totalRecords, setPage, setPageSize, paginatedItems: pageRates } = usePagination({
    items: rates,
    initialPageSize: 25,
    resetDeps: [selectedPanelId, selectedDept, search, itemTypeFilter],
  });

  return (
    <div className="mst-tab-content">
      {/* Title bar */}
      <div className="pwr-head">
        <div className="pwr-head-title">
          <span className="pwr-head-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </span>
          <div>
            <h2>Panel-Wise Rate List</h2>
            <p>Set the net rate of Tests, Clinical Profiles and Health Packages for a Cash Billing Panel. Net Rate = MRP − Discount.</p>
          </div>
        </div>
        <div className="pwr-head-actions">
          {dirtyCount > 0 && <span className="pwr-dirty">● {dirtyCount} Unsaved Changes</span>}
          <button type="button" className="mst-btn-secondary" onClick={fetchPanelRates} disabled={loading || saving}>
            ↻ Refresh
          </button>
          <button type="button" className="mst-btn-primary" onClick={handleSaveAll} disabled={dirtyCount === 0 || saving} style={{ minWidth: 140 }}>
            {saving ? 'Saving...' : `Save Rates (${dirtyCount})`}
          </button>
        </div>
      </div>

      {/* Control card: tariff views, panel, search, department */}
      <div className="pwr-card">
        <div className="pwr-viewbar">
          <div className="pwr-views" role="tablist" aria-label="Tariff item view">
            {TARIFF_VIEWS.map((v) => {
              const active = itemTypeFilter === v.key;
              // the count of the view that is open is the number of rows loaded; the others are only known while all items are loaded
              const count = v.key === 'ALL' ? (active ? typeCounts.all : null) : (itemTypeFilter === 'ALL' ? typeCounts[v.countKey] : typeCounts.all);
              return (
                <button
                  key={v.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`pwr-view ${v.className} ${active ? 'is-active' : ''}`}
                  onClick={() => switchView(v.key)}
                >
                  <span aria-hidden="true">{v.icon}</span>
                  {v.label}
                  {count !== null && <span className="pwr-count">{count}</span>}
                </button>
              );
            })}
          </div>

          <div className="pwr-context">
            <span className="pwr-chip">Panel <b>{currentPanel?.panelName || 'Walk-In'}</b>{currentPanel?.panelCode ? ` (${currentPanel.panelCode})` : ''}</span>
            {currentPanel?.referenceCodeOPD && currentPanel.referenceCodeOPD !== currentPanel.panelId ? (
              <span className="pwr-chip pwr-chip-info" title="Items without their own rate take the rate of the reference panel">
                <span className="pwr-chip-dot" aria-hidden="true" />Inherits from #{currentPanel.referenceCodeOPD}
              </span>
            ) : (
              <span className="pwr-chip pwr-chip-good" title="This panel keeps its own rates">
                <span className="pwr-chip-dot" aria-hidden="true" />Independent Tariff
              </span>
            )}
          </div>
        </div>

        <div className="pwr-filters">
          <div className="pwr-field">
            <label className="pwr-label" htmlFor="pwr-panel">Cash Billing Panel</label>
            <div className="pwr-control">
              <span className="pwr-control-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h1M9 13h1M14 9h1M14 13h1M10 21v-4h4v4" /></svg>
              </span>
              <select
                id="pwr-panel"
                className="pwr-input"
                value={selectedPanelId}
                onChange={(e) => {
                  if (dirtyCount > 0 && !window.confirm('You have unsaved rate changes. Switching panels will discard them. Continue?')) {
                    return;
                  }
                  setSelectedPanelId(Number(e.target.value));
                }}
                disabled={panelsLoading || loading}
              >
                {panels.map((p) => (
                  <option key={p.panelId} value={p.panelId}>
                    {p.panelName} ({p.panelCode}) — {p.centreName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="pwr-field">
            <label className="pwr-label" htmlFor="pwr-search">Search Item / Code</label>
            <div className="pwr-control">
              <span className="pwr-control-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              </span>
              <input
                id="pwr-search"
                type="text"
                className="pwr-input"
                placeholder="Search by item name or code..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="pwr-field">
            <label className="pwr-label" htmlFor="pwr-dept">Department</label>
            <div className="pwr-control">
              <span className="pwr-control-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /></svg>
              </span>
              <select id="pwr-dept" className="pwr-input" value={selectedDept} onChange={(e) => setSelectedDept(e.target.value)}>
                <option value="all">All Departments ({departments.length})</option>
                {departments.map((d) => (
                  <option key={d.subCategoryId} value={d.subCategoryId}>{d.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Bulk Action Strip (Visible when items selected) */}
        {selectedItemIds.size > 0 && (
          <div className="pwr-bulk">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b' }}>
                ✓ {selectedItemIds.size} items selected
              </span>
              <button
                type="button"
                className="mst-btn-secondary"
                style={{ fontSize: '0.78rem', padding: '4px 8px' }}
                onClick={() => setSelectedItemIds(new Set())}
              >
                Clear Selection
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* Fixed Rate Input */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '0.82rem', color: '#64748b' }}>Set Fixed Rate: ₹</span>
                <input
                  type="number"
                  placeholder="300"
                  style={{ width: 90, padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.88rem' }}
                  value={bulkRateInput}
                  onChange={(e) => setBulkRateInput(e.target.value)}
                />
                <button
                  type="button"
                  className="mst-btn-primary"
                  style={{ fontSize: '0.82rem', padding: '5px 12px' }}
                  onClick={() => {
                    const rate = normaliseRate(bulkRateInput);
                    if (rate === null) {
                      showToast?.(`Please enter a rate from 0 to ${MAX_PANEL_RATE}.`, 'error');
                      return;
                    }
                    setModifiedRows(prev => {
                      const next = { ...prev };
                      rates.forEach(r => {
                        if (selectedItemIds.has(r.itemId)) {
                          next[r.itemId] = {
                            itemId: r.itemId,
                            rate: rate,
                            mrpRate: r.mrpRate,
                            itemDisplayName: r.itemDisplayName || r.investigationName,
                            itemCode: r.testCode
                          };
                        }
                      });
                      return next;
                    });
                    showToast?.(`Applied ₹${rate} to ${selectedItemIds.size} items. Click 'Save Rates' to persist.`, 'success');
                  }}
                  disabled={!bulkRateInput}
                >
                  Apply Rate
                </button>
              </div>

              <span style={{ color: '#cbd5e1' }}>|</span>

              {/* Discount % Input */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '0.82rem', color: '#64748b' }}>Discount:</span>
                <input
                  type="number"
                  placeholder="20"
                  style={{ width: 70, padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.88rem' }}
                  value={bulkDiscountInput}
                  onChange={(e) => setBulkDiscountInput(e.target.value)}
                />
                <span style={{ fontSize: '0.82rem', color: '#64748b' }}>% off MRP</span>
                <button
                  type="button"
                  className="mst-btn-primary"
                  style={{ fontSize: '0.82rem', padding: '5px 12px' }}
                  onClick={() => {
                    const pct = isValidDiscount(String(bulkDiscountInput).trim()) ? Number(String(bulkDiscountInput).trim()) : NaN;
                    if (isNaN(pct)) {
                      showToast?.('Please enter a discount percentage between 0 and 100.', 'error');
                      return;
                    }
                    const { updates, skipped } = planBulkDiscount(rates, selectedItemIds, pct);
                    setModifiedRows(prev => {
                      const next = { ...prev };
                      updates.forEach(u => { next[u.itemId] = u; });
                      return next;
                    });
                    showToast?.(bulkDiscountMessage(pct, updates.length, skipped), 'success');
                  }}
                  disabled={!bulkDiscountInput}
                >
                  Apply %
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Rates Grid */}
      <div style={{ backgroundColor: '#ffffff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
        <table className="mst-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <th style={{ width: 40, padding: '10px 14px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={rates.length > 0 && selectedItemIds.size === rates.length}
                  onChange={toggleSelectAll}
                  title={`Select all ${rates.length} items (every page)`}
                  aria-label={`Select all ${rates.length} items on every page`}
                />
              </th>
              <th style={{ width: 45, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase' }}>#</th>
              <th style={{ width: 90, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', textAlign: 'center' }}>Category</th>
              <th style={{ width: 110, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase' }}>Item Code</th>
              <th style={{ padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase' }}>Item / Tariff Name</th>
              <th style={{ width: 160, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase' }}>Department</th>
              <th style={{ width: 120, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', textAlign: 'right' }}>Standard MRP</th>
              <th style={{ width: 120, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', textAlign: 'right' }}>Current Rate</th>
              <th style={{ width: 130, padding: '10px 14px', fontSize: '0.78rem', color: '#0f172a', textTransform: 'uppercase', textAlign: 'right' }} title="Percentage off the standard MRP. Net Rate = MRP − Discount. Type a percentage to set the Net Rate.">Discount (% off MRP)</th>
              <th style={{ width: 150, padding: '10px 14px', fontSize: '0.78rem', color: '#0f172a', textTransform: 'uppercase', textAlign: 'right' }} title="Net Rate = MRP − Discount. Type a rate to change it.">Net Rate (₹)</th>
              <th style={{ width: 85, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', textAlign: 'center' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                  <div style={{ display: 'inline-block', width: 24, height: 24, border: '3px solid #cbd5e1', borderTopColor: '#0284c7', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  <div style={{ marginTop: 8, fontSize: '0.88rem' }}>Loading panel rate schedules...</div>
                </td>
              </tr>
            ) : rates.length === 0 ? (
              <tr>
                <td colSpan="11" style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                  No items found matching your filter criteria.
                </td>
              </tr>
            ) : (
              pageRates.map((item, idx) => {
                const isSelected = selectedItemIds.has(item.itemId);
                const isModified = modifiedRows[item.itemId] !== undefined;
                const activeRate = isModified ? modifiedRows[item.itemId].rate : item.panelRate;
                const type = (item.itemType || 'TEST').toUpperCase();

                return (
                  <tr
                    key={item.itemId}
                    style={{
                      borderBottom: '1px solid #f1f5f9',
                      backgroundColor: isModified ? '#fffbeb' : isSelected ? '#f0f9ff' : idx % 2 === 0 ? '#ffffff' : '#fafafa'
                    }}
                  >
                    <td style={{ textAlign: 'center', padding: '8px 14px' }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectItem(item.itemId)}
                      />
                    </td>
                    <td style={{ padding: '8px 14px', color: '#94a3b8', fontSize: '0.82rem' }}>{(page - 1) * pageSize + idx + 1}</td>
                    
                    {/* Item Type Badge */}
                    <td style={{ padding: '8px 14px', textAlign: 'center' }}>
                      <span style={{
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        padding: '3px 8px',
                        borderRadius: 6,
                        backgroundColor: type === 'PACKAGE' ? '#ecfdf5' : type === 'PROFILE' ? '#faf5ff' : '#f0f9ff',
                        color: type === 'PACKAGE' ? '#047857' : type === 'PROFILE' ? '#7e22ce' : '#0369a1',
                        border: type === 'PACKAGE' ? '1px solid #a7f3d0' : type === 'PROFILE' ? '1px solid #e9d5ff' : '1px solid #bae6fd',
                        display: 'inline-block'
                      }}>
                        {type}
                      </span>
                    </td>

                    <td style={{ padding: '8px 14px' }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#0f172a', backgroundColor: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: '0.8rem' }}>
                        {item.testCode || 'N/A'}
                      </span>
                    </td>

                    <td style={{ padding: '8px 14px', fontWeight: 600, color: '#1e293b' }}>
                      <div>{item.investigationName}</div>
                      {type === 'PACKAGE' && (
                        <div style={{ fontSize: '0.74rem', color: '#059669', fontWeight: 500, marginTop: 2 }}>
                          📦 Health Package • {item.activeComponentCount || 0} Components included
                        </div>
                      )}
                      {type === 'PROFILE' && (
                        <div style={{ fontSize: '0.74rem', color: '#7e22ce', fontWeight: 500, marginTop: 2 }}>
                          📋 Clinical Profile • {item.activeComponentCount || 0} Tests included
                        </div>
                      )}
                      {item.itemDisplayName && item.itemDisplayName !== item.investigationName && (
                        <div style={{ fontSize: '0.74rem', color: '#64748b' }}>Alt: {item.itemDisplayName}</div>
                      )}
                    </td>

                    <td style={{ padding: '8px 14px', fontSize: '0.84rem', color: '#475569' }}>
                      {item.departmentName}
                    </td>

                    <td style={{ padding: '8px 14px', textAlign: 'right', fontSize: '0.88rem', color: '#64748b' }}>
                      ₹{item.mrpRate.toFixed(2)}
                    </td>

                    <td style={{ padding: '8px 14px', textAlign: 'right', fontSize: '0.88rem', fontWeight: 600, color: '#334155' }}>
                      ₹{item.panelRate.toFixed(2)}
                    </td>

                    <td style={{ padding: '8px 14px', textAlign: 'right' }}>
                      <DiscountCell
                        mrp={item.mrpRate}
                        rate={activeRate}
                        highlighted={isModified}
                        label={item.investigationName}
                        onApply={(pct) => handleRateChange(item, rateFromDiscount(item.mrpRate, pct))}
                      />
                    </td>

                    <td style={{ padding: '8px 14px', textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>₹</span>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          style={{
                            width: 95,
                            padding: '6px 8px',
                            textAlign: 'right',
                            fontWeight: 700,
                            fontSize: '0.92rem',
                            borderRadius: 6,
                            border: isModified ? '2px solid #f59e0b' : '1px solid #cbd5e1',
                            backgroundColor: isModified ? '#ffffff' : '#f8fafc',
                            color: isModified ? '#b45309' : '#0f172a'
                          }}
                          value={activeRate}
                          onChange={(e) => handleRateChange(item, e.target.value)}
                        />
                      </div>
                    </td>

                    <td style={{ padding: '8px 14px', textAlign: 'center' }}>
                      {isModified ? (
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 12, backgroundColor: '#fef3c7', color: '#d97706' }}>
                          DIRTY
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 12, backgroundColor: '#dcfce7', color: '#16a34a' }}>
                          SAVED
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!loading && rates.length > 0 && (
        <CommonPagination
          page={page}
          pageSize={pageSize}
          totalRecords={totalRecords}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[25, 50, 100, 200]}
        />
      )}

      {/* Safety Bulk Confirmation Modal */}
      <ConfirmModal
        isOpen={showBulkConfirm}
        title="Confirm Bulk Rate Update"
        message={
          bulkActionType === 'fixed'
            ? `You are about to apply a fixed rate of ₹${bulkRateInput} to ${selectedItemIds.size} selected items for panel '${currentPanel?.panelName}'. These changes will remain in dirty state until you click 'Save Rates'.`
            : `You are about to apply a ${bulkDiscountInput}% discount off MRP across ${selectedItemIds.size} selected items for panel '${currentPanel?.panelName}'.`
        }
        confirmLabel="Apply to Selected"
        cancelLabel="Cancel"
        onConfirm={handleApplyBulk}
        onCancel={() => setShowBulkConfirm(false)}
      />
    </div>
  );
};

export default PanelWiseRateTab;
